import { client } from './api';
import { downloadResponseAsFile } from '../utils/downloadFile';

/**
 * Clinician logins across every customer agency, read from Roger Docs.
 *
 * Reached only from Internal Tools, which already requires a Strawhat super
 * user. Every string is "" when Docs has no value.
 */
export interface ClinicianDirectoryAgency {
  agency_id: string;
  written_name: string;
}

export interface ClinicianDirectoryRow {
  agency_id: string;
  agency_name: string;
  username: string;
  first_name: string;
  last_name: string;
  title: string;
  email: string;
  phone_number: string;
  active: boolean;
}

export interface ClinicianDirectoryPage {
  total: number;
  items: ClinicianDirectoryRow[];
}

export interface ClinicianDirectoryFilters {
  /** Empty means every agency. */
  agencyIds: string[];
  text: string;
  includeInactive: boolean;
}

/** Shorter text is not sent: each search re-reads the whole Docs roster. */
export const MIN_SEARCH_LENGTH = 2;
/** The API refuses longer text. */
export const MAX_SEARCH_LENGTH = 100;
/** The API refuses more agency ids; none picked means every agency. */
export const MAX_AGENCY_FILTER = 200;

export function directoryFilterQuery(filters: ClinicianDirectoryFilters): {
  agency_id?: string[];
  q?: string;
  include_inactive: boolean;
} {
  const q = filters.text.trim();
  return {
    ...(filters.agencyIds.length > 0 ? { agency_id: filters.agencyIds } : {}),
    ...(q.length >= MIN_SEARCH_LENGTH ? { q } : {}),
    include_inactive: filters.includeInactive,
  };
}

export async function listClinicianDirectoryAgencies(): Promise<
  ClinicianDirectoryAgency[]
> {
  const response = await client.get<
    { 200: { agencies: ClinicianDirectoryAgency[] } },
    unknown,
    true
  >({
    responseType: 'json',
    throwOnError: true,
    url: '/internal-tools/clinician-directory/agencies',
  });
  return response.data.agencies;
}

export async function searchClinicianDirectory(
  filters: ClinicianDirectoryFilters,
  offset: number,
  limit: number
): Promise<ClinicianDirectoryPage> {
  const response = await client.get<
    { 200: ClinicianDirectoryPage },
    unknown,
    true
  >({
    query: { ...directoryFilterQuery(filters), offset, limit },
    responseType: 'json',
    throwOnError: true,
    url: '/internal-tools/clinician-directory',
  });
  return response.data;
}

export async function downloadClinicianDirectoryCsv(
  filters: ClinicianDirectoryFilters
): Promise<void> {
  const response = await client.get<Blob, unknown, true>({
    query: directoryFilterQuery(filters),
    responseType: 'blob',
    throwOnError: true,
    url: '/internal-tools/clinician-directory/export.csv',
  });
  downloadResponseAsFile(response, 'Roger_Clinician_Directory.csv');
}
